/**
 * What is in the application's left rail, written down once.
 *
 * It used to be written twice: a desktop <aside> and a mobile drawer, each
 * with its own copy of every item, in two different sets of Tailwind classes,
 * 200 lines apart in Layout.tsx. They had already drifted. The drawer carried
 * Help and four Resources links that the rail did not, and nothing said
 * whether that was a decision or an oversight.
 *
 * It was a decision, and now it is a legible one: the header holds Help and
 * Resources on a wide screen and hides them on a phone, so the drawer picks
 * them up under a head that says where they came from. Everything else is one
 * list rendered in two places, and adding an entry adds it to both.
 */

import type { ComponentType } from 'react'
import { Globe, Map, Settings, Shield, Sparkles } from 'lucide-react'
import { SideLink } from '@/design-system/app'
import { Tag } from '@/design-system/marks'
import HubSpotIcon from './icons/HubSpotIcon'

export interface AppNavEntry {
  name: string
  href: string
  /* lucide takes `aria-hidden`; HubSpotIcon takes only `className`. Both are
     drawn at 17px by `.ds-side__link svg`, so neither is passed a size. */
  icon: ComponentType<{ 'aria-hidden'?: boolean | 'true' | 'false'; className?: string }>
  /** Shown beside the name. A fact about the entry, never about its state. */
  mark?: string
  /** Whether this entry is the page you are on. */
  isHere: (pathname: string) => boolean
  /** Which accounts see it at all. */
  when?: 'chief' | 'admin'
}

/** Current anywhere below the path, which is what a section link means. */
const under = (href: string) => (pathname: string) =>
  pathname === href || pathname.startsWith(href + '/')

/** Current at exactly this page and nowhere else. */
const exactly = (href: string) => (pathname: string) => pathname === href

/*
  Order is the order a person meets them: the place they work, the thing that
  works for them, then their sites, then the things they set up once.

  Chief of Staff ships to accounts before it is finished, and the mark is the
  only place the app says so. Dropping it would be silent, so
  Layout.chiefNav.test.tsx holds both the mark and this position.
*/
export const APP_NAV: AppNavEntry[] = [
  { name: 'Dashboard', href: '/dashboard', icon: Globe, isHere: under('/dashboard') },
  {
    name: 'Chief of Staff',
    href: '/chief',
    icon: Sparkles,
    mark: 'Beta',
    when: 'chief',
    isHere: exactly('/chief'),
  },
]

/** Below the sites: the things you set up once and then stop thinking about. */
export const APP_NAV_SETUP: AppNavEntry[] = [
  { name: 'Settings', href: '/settings', icon: Settings, isHere: under('/settings') },
  { name: 'HubSpot', href: '/hubspot', icon: HubSpotIcon, isHere: exactly('/hubspot') },
  { name: 'Admin', href: '/admin', icon: Shield, when: 'admin', isHere: under('/admin') },
]

/** Pinned to the bottom of the rail, away from the work. */
export const APP_NAV_FOOT: AppNavEntry[] = [
  { name: 'Roadmap', href: '/roadmap', icon: Map, isHere: exactly('/roadmap') },
]

/*
  On a wide screen these live in the header. The phone header has room for
  notifications, the theme and the account and nothing else, so the drawer
  carries them instead, under a head that says what they are.
*/
export const APP_NAV_READING = [
  { name: 'AEO Guide', href: '/aeo' },
  { name: 'GEO Guide', href: '/geo' },
  { name: 'AI Search Optimization', href: '/ai-search-optimization' },
  { name: 'Schema Markup Guide', href: '/schema-markup' },
]

export function AppNavItem({
  entry,
  pathname,
  onGo,
}: {
  entry: AppNavEntry
  pathname: string
  onGo?: () => void
}) {
  const Icon = entry.icon
  return (
    <SideLink
      to={entry.href}
      current={entry.isHere(pathname)}
      onClick={onGo}
      icon={<Icon aria-hidden="true" />}
      after={entry.mark ? <Tag>{entry.mark}</Tag> : undefined}
    >
      {entry.name}
    </SideLink>
  )
}

/** The entries an account can actually see, in order. */
export function visibleEntries(
  entries: AppNavEntry[],
  { showChief, isAdmin }: { showChief: boolean; isAdmin: boolean }
): AppNavEntry[] {
  return entries.filter((entry) => {
    if (entry.when === 'chief') return showChief
    if (entry.when === 'admin') return isAdmin
    return true
  })
}
